import { GameObjects, Scene } from 'phaser';
import { StaffTransition, VoiceGraphic } from '.';
import { ClefData, ClefType, Config } from './Types';
import TimeSignature from 'Models/TimeSignature';
import { ProgressState } from '../Progress';
import { BaseNote, getMidiNumberFromNoteTuple } from 'Phaser/Util/OSMDHelper';
import RestGraphic from './Rest';
import NoteGraphic from './Note';
import ClefMeasure, { MeasureDataExtra } from './Measure';
import { addOptional } from 'Phaser/util';
import KeySignatureGraphic from './KeySignature';

export default class Clef extends GameObjects.Container {
  pitchLines: GameObjects.Rectangle[] = [];
  barWidths: number[] = [];
  notes: Map<string, VoiceGraphic> = new Map();
  noteArray: VoiceGraphic[] = [];
  _data: ClefData;
  measureData: MeasureDataExtra[];
  config: Config;
  clefSymbol?: Phaser.GameObjects.Image;
  keySignatureGraphic?: KeySignatureGraphic;
  timeSignatureGraphics: Phaser.GameObjects.Image[] = [];
  tweenHandles: Phaser.Tweens.Tween[] = [];
  timeSig?: TimeSignature;
  measures: ClefMeasure[] = [];

  highestNoteLine: number;
  lowestNoteLine: number;

  /** DPR used for rendering. Passed from parent PhraseGraphic.
   *  Setting this value propagates it to child Note and Rest components. */
  private _renderDpr: number = window.devicePixelRatio;

  get renderDpr(): number {
    return this._renderDpr;
  }

  set renderDpr(value: number) {
    this._renderDpr = value;
    this.noteArray.forEach((note) => {
      note.renderDpr = value;
    });
  }

  innerBoxGizmo?: Phaser.GameObjects.Rectangle;
  outerBoxGizmo?: Phaser.GameObjects.Rectangle;

  constructor(
    scene: Scene,
    config: Config,
    data: ClefData,
    measureData: MeasureDataExtra[],
    renderDpr: number = window.devicePixelRatio,
    timeSig?: TimeSignature,
    clefSymbol?: Phaser.GameObjects.Image,
    timeSignature?: Phaser.GameObjects.Image[],
    keySignatureFifths: number = 0,
  ) {
    super(scene);
    this._renderDpr = renderDpr;
    this.timeSig = timeSig;
    this.measureData = measureData;
    this.config = config;
    this._data = data;
    if (keySignatureFifths !== 0) {
      this.keySignatureGraphic = new KeySignatureGraphic(
        scene,
        data.clef,
        keySignatureFifths,
        Phaser.Display.Color.ValueToColor(config.notationColor).color,
      );
      this.keySignatureGraphic.setAlpha(0);
      this.add(this.keySignatureGraphic);
    }
    scene.add.existing(this);
    this.createLines(measureData);
    this.setPosition(0, data.clef === 'bass' ? config.pitchLineGap * 4 + config.clefGap : 0);
    let highestNoteLine = 0;
    let lowestNoteLine = 8;
    data.notes.forEach((n) => {
      if (n.voice !== 'rest') {
        const num = getNoteLineNumber(data.octaveOffset, data.clef, n.voice.note);
        if (num < highestNoteLine) highestNoteLine = num;
        else if (num > lowestNoteLine) lowestNoteLine = num;
      }
    });
    this.highestNoteLine = highestNoteLine;
    this.lowestNoteLine = lowestNoteLine;
    this.reset(data, measureData, clefSymbol, timeSignature);
  }

  getWidth() {
    return this.pitchLines[0].width;
  }

  transitionOut() {
    this.addTween({
      targets: this.pitchLines,
      alpha: 0,
      duration: 500,
    });
    this.noteArray.forEach((note, _idx) => note.transitionOut());
  }

  transition(style: StaffTransition) {
    this.addTween({
      targets: [this.clefSymbol, this.keySignatureGraphic, ...this.timeSignatureGraphics],
      alpha: 1,
      duration: style === 'None' ? 0 : 500,
    });
    switch (style) {
      case 'Draw': {
        const width = this.pitchLines[0].width;
        this.pitchLines.forEach((line, idx) => {
          line.width = 0;
          this.addTween({
            targets: line,
            delay: 50 * idx,
            alpha: 1,
            width,
            duration: 1000,
            ease: 'Power2',
          });
        });
        this.addTween({
          targets: [this.clefSymbol],
          alpha: 1,
          duration: 500,
          delay: 500,
          ease: 'Power2',
        });
        this.noteArray.forEach((note, idx) => note.transition(style, idx));
        break;
      }
      case 'Fade':
        this.addTween({
          targets: this.pitchLines,
          alpha: 1,
          duration: 500,
        });
        this.noteArray.forEach((note, idx) => note.transition(style, idx));
        break;
      case 'None':
        this.addTween({
          targets: this.pitchLines,
          alpha: 1,
          duration: 0,
        });
        this.noteArray.forEach((note, idx) => note.transition(style, idx));
        break;
    }
  }

  addTween(tween: Phaser.Tweens.Tween | Phaser.Types.Tweens.TweenBuilderConfig) {
    this.tweenHandles.push(this.scene.tweens.add(tween));
  }

  checkAllNotes() {
    for (const note of this.notes) {
      if (note[1].isRest) {
        continue;
      }
      if (!Object.keys(note[1]).contains('timestamp')) {
        continue;
      }

      const isNotYetPlayed = note[1].timingState === undefined;
      if (isNotYetPlayed) {
        console.warn('undefined', note[1].timingState);
        return undefined;
      }

      // Only perfectly timed notes pass. Semi-correct notes (LateStart, MistimedRelease)
      // show yellow feedback but still fail the exercise.
      if (note[1].timingState !== ProgressState.Ended) {
        return false;
      }
    }
    return true;
  }

  destroy() {
    this.pitchLines.forEach((l) => l.destroy());
    this.pitchLines.clear();
    this.barWidths.clear();
    this.measures.forEach((m) => m.destroy());
    this.measures.clear();
    //this.notes.forEach((note) => note.destroy());
    this.notes.clear();
    this.timeSignatureGraphics?.map((t) => t.destroy());
    this.timeSignatureGraphics?.clear();
    this.keySignatureGraphic?.destroy();
    this.keySignatureGraphic = undefined;
    this.clefSymbol?.destroy();
  }

  reset(
    data: ClefData,
    measureData: MeasureDataExtra[],
    clefSymbol?: Phaser.GameObjects.Image,
    timeSignature?: Phaser.GameObjects.Image[],
  ) {
    this._data = data;
    this.measureData = measureData;
    if (clefSymbol) {
      this.clefSymbol = clefSymbol;
      this.add(clefSymbol);
      this.clefSymbol?.setAlpha(0);
    }
    if (timeSignature) {
      this.timeSignatureGraphics = timeSignature;
      this.timeSignatureGraphics.forEach((g) => {
        g.setAlpha(0);
        this.add(g);
      });
    }
    this.positionExtraSymbols();
    this.createNotes(measureData);
  }

  getFirstNotePosition(offset: number) {
    return this.measures[offset].getFirstNotePosition() ?? Infinity + this.x;
  }

  getFirstNoteId(): string | undefined {
    return this.measures[0]?.getFirstNoteId();
  }

  getFirstNoteInMeasure(measure: number): number | undefined {
    console.debug('got first', this.measures);
    return addOptional(this.measures[measure].getFirstNotePosition(), this.x);
  }

  getNotePosition(id: string, timestamp?: number, _rest?: boolean) {
    const note = this.notes.get(id);
    if (
      note &&
      (timestamp === undefined ||
        (note.timestamp ?? Infinity) + this._data.startTimestamp === timestamp)
    ) {
      const x = note.x + this.measures[note.measureIdx].x + this.x;
      const y = note.getBoundsY() + this.measures[note.measureIdx].y + this.y;
      return { x, y };
    }
    if (note && timestamp !== undefined) {
      console.warn(
        `[Clef.getNotePosition] id=${id} timestamp mismatch: requested=${timestamp} noteAbsTs=${(note.timestamp ?? Infinity) + this._data.startTimestamp} (noteTs=${note.timestamp} + clefStartTs=${this._data.startTimestamp})`,
      );
    }
    /*if (
      note &&
      (timestamp === undefined ||
        (note.timestamp ?? Infinity) + this._data.startTimestamp === timestamp)
    ) {
      return note.getPosition().x + this.x;
    }*/
  }

  getNote(id: string, timestamp?: number) {
    const note = this.notes.get(id);
    if (timestamp !== undefined && timestamp !== null && note != null) {
      const noteAbsoluteTimestamp = note.timestamp! + this._data.startTimestamp;
      if (noteAbsoluteTimestamp === timestamp) {
        return note;
      } else {
        console.log(
          `[Clef.getNote] id=${id} timestamp mismatch: requested=${timestamp} note=${noteAbsoluteTimestamp} (noteTs=${note.timestamp} + startTs=${this._data.startTimestamp})`,
        );
        return null;
      }
    } else if (note != null && timestamp === undefined) {
      console.warn(
        `[Clef.getNote] id=${id} found WITHOUT timestamp check (timestamp was undefined), clef startTs=${this._data.startTimestamp}`,
      );
      return note;
    } else {
      return note;
    }
  }

  createNotes(measureData: MeasureDataExtra[]) {
    const { globalScaling: _globalScaling } = this.config;
    this.noteArray = this._data.notes
      .sort((a, b) => a.x - b.x)
      .map(
        (
          {
            x,
            voice,
            length,
            id,
            timestamp,
            measureIdx,
            fingering,
            fingeringNeed,
            accidentalSymbol: explicitAccidentalSymbol,
            midiNumber,
          },
          idx,
        ) => {
          const xPos =
            x +
            measureData[measureIdx].firstNoteRelativeOffset! +
            //measureData[measureIdx].left!;
            measureData[measureIdx].extraGap;
          console.debug('fingering data', fingering);
          if (voice === 'rest') {
            return new RestGraphic(
              this.scene,
              measureIdx,
              id,
              xPos,
              x,
              4,
              length,
              this.config,
              idx,
              this.timeSig!.numerator,
              timestamp,
              this.renderDpr,
              this.config.notationColor,
            );
          } else {
            const line = getNoteLineNumber(this._data.octaveOffset, this._data.clef, voice.note);
            const accidentalSymbol =
              explicitAccidentalSymbol !== undefined
                ? (explicitAccidentalSymbol ?? undefined)
                : voice.note[0].includes('#')
                  ? '#'
                  : voice.note[0].includes('b')
                    ? 'b'
                    : undefined;
            return new NoteGraphic(
              this.scene,
              measureIdx,
              id,
              xPos,
              x,
              line,
              length,
              this.config,
              timestamp,
              idx,
              midiNumber ?? getMidiNumberFromNoteTuple(voice.note),
              this,
              fingering,
              accidentalSymbol,
              this.config.notationColor,
              fingeringNeed,
            );
          }
        },
      )
      .sort((a, b) => a.x - b.x);
    const { pitchLineGap } = this.config;
    this.measures = measureData.map(
      (m, idx) =>
        new ClefMeasure(
          this.scene,
          m,
          this.noteArray.filter((n) => n.measureIdx === idx),
          pitchLineGap,
          this._data.clef,
          this,
        ),
    );
    this.noteArray.forEach((n) => {
      if (n) {
        this.notes.set(n.id, n);
        n.setAlpha(0);
      }
    });
  }

  getReferenceLineY() {
    return this.pitchLines[this.pitchLines.length - 1].y;
  }

  createLines(measureData: MeasureDataExtra[]) {
    const { pitchLineGap, pitchLineWidth } = this.config;
    const staffLineColor = Phaser.Display.Color.ValueToColor(this.config.staffLineColor).color;
    const phraseWidth = measureData.reduce(
      (prev, m) => prev + m.width + m.firstNoteRelativeOffset + m.extraGap,
      0,
    );
    this.pitchLines = [0, 1, 2, 3, 4].map(
      (i) =>
        new GameObjects.Rectangle(
          this.scene,
          0,
          i * pitchLineGap,
          phraseWidth,
          pitchLineWidth,
          staffLineColor,
          1,
        ),
    );
    this.pitchLines.forEach((line) => {
      this.add(line);
      line.setOrigin(0, 0);
      line.setAlpha(0);
    });
  }

  newResize(
    width: number,
    innerHeight: number,
    outerHeight: number,
    config: Config,
    x: number,
    extraWidth: number,
    clefExtraLinesTop: number,
    clefExtraLinesBottom: number,
    offsetY?: number,
  ) {
    const dpr = this.renderDpr;
    this.config = config;
    this.setX(x);
    const { pitchLineGap, pitchLineWidth } = config;
    this.pitchLines.forEach((l, idx) => {
      l.setY(idx * pitchLineGap * dpr);
      l.setSize(width, pitchLineWidth * dpr);
    });
    // Layout invariant: the per-measure leading gap stays at a fixed CSS size
    // (rendered as `firstNoteRelativeOffset * dpr` canvas pixels). Whatever
    // canvas width remains after the gaps + clef extras is split among the
    // measure content via measuresXScale. Earlier versions mixed canvas and
    // CSS units (xScale = canvas / CSS, gap = CSS * dpr) so the summed
    // measure widths didn't add up to the available width — content either
    // fell short of, or overflowed past, the rightmost barline.
    const sumMeasureWidthsCss = this.measures.reduce((prev, m) => prev + m.measureData.width, 0);
    const sumGapsCss = this.measures.reduce(
      (prev, m) => prev + m.measureData.firstNoteRelativeOffset,
      0,
    );
    const naturalCanvasWidth = (sumMeasureWidthsCss + sumGapsCss) * dpr;
    const availableCanvas = Math.max(0, width - extraWidth);
    let measuresXScale: number;
    let gapScale: number;
    if (naturalCanvasWidth <= availableCanvas && sumMeasureWidthsCss > 0) {
      // Wide enough: hold the gap at its natural CSS size, stretch note
      // content to fill the remaining canvas. sum(fullWidth) == width.
      const stretchAvailableCanvas = availableCanvas - sumGapsCss * dpr;
      measuresXScale = stretchAvailableCanvas / sumMeasureWidthsCss;
      gapScale = 1;
    } else if (naturalCanvasWidth > 0) {
      // Squeezed: shrink content AND gaps proportionally so everything fits.
      const shrinkFactor = availableCanvas / naturalCanvasWidth;
      measuresXScale = dpr * shrinkFactor;
      gapScale = shrinkFactor;
    } else {
      measuresXScale = 1;
      gapScale = 1;
    }
    this.measures.reduce(
      (prev, m, idx) =>
        prev +
        m.resize(
          measuresXScale,
          prev,
          pitchLineGap,
          m.measureData.firstNoteRelativeOffset * gapScale * dpr,
          idx === 0 ? extraWidth : 0,
        ),
      0,
    );
    if (this._data.clef === 'bass') {
      const y = (offsetY ?? 0) - (-clefExtraLinesTop / 2) * config.pitchLineGap * dpr;
      this.setY(y);
    }
    this.positionExtraSymbols();
    this.drawGizmos(width, innerHeight * this.renderDpr, outerHeight * this.renderDpr);
    console.debug('this y', this.y);
    if (this._data.clef === 'treble') {
      this.outerBoxGizmo?.setY(-(clefExtraLinesTop / 2) * pitchLineGap * dpr);
    } else {
      this.innerBoxGizmo?.setY(-(clefExtraLinesTop / 2) * pitchLineGap * dpr);
      this.outerBoxGizmo?.setY(-(clefExtraLinesTop / 2) * pitchLineGap * dpr);
    }
  }

  drawGizmos(width: number, innerHeight: number, outerHeight: number) {
    console.debug('REACT_APP_DRAW_GIZMOS', process.env.REACT_APP_DRAW_GIZMOS);
    if (process.env.REACT_APP_DRAW_GIZMOS === 'true') {
      this.innerBoxGizmo?.destroy();
      this.outerBoxGizmo?.destroy();
      this.innerBoxGizmo = this.scene.add.rectangle(0, 0, width, innerHeight, 0x00ff00, 0.5);
      this.outerBoxGizmo = this.scene.add.rectangle(0, 0, width, outerHeight, 0xff0000, 0.5);
      this.innerBoxGizmo.setOrigin(0, 0);
      this.outerBoxGizmo.setOrigin(0, 0);
      this.add(this.innerBoxGizmo);
      this.add(this.outerBoxGizmo);
    }
  }

  getHighestLineY() {
    if (this._data.clef === 'treble') return this.pitchLines[this.pitchLines.length - 1].y;
    else return this.pitchLines[0].y;
  }

  getLowestLineY() {
    if (this._data.clef === 'treble') return this.pitchLines[0].y;
    else return this.pitchLines[this.pitchLines.length - 1].y;
  }

  resize(width: number, height: number, clefSpacing: number, x: number, extraWidth: number) {
    const dpr = this.renderDpr;
    this.setX(x);
    const pitchLineGap = height / 4;
    this.pitchLines.forEach((l, idx) => {
      l.setY(idx * pitchLineGap);
      l.setSize(width, l.height);
    });
    // Gaps are rendered at gap * dpr (CSS pixels → render pixels). Compute
    // the scale factor so the total rendered width exactly fills the target.
    //   - Roomy (natural content ≤ target): gaps stay at natural DPR size,
    //     notes expand to fill the remaining space.
    //   - Tight (natural content > target): both notes and gaps shrink
    //     proportionally to fit.
    const totalNoteWidth = this.measures.reduce((prev, m) => prev + m.measureData.width, 0);
    const totalGapWidthDpr = this.measures.reduce(
      (prev, m) => prev + m.measureData.firstNoteRelativeOffset * dpr,
      0,
    );
    const availableForContent = width - extraWidth;
    let measuresXScale: number;
    let gapScale: number;
    if (totalNoteWidth + totalGapWidthDpr <= availableForContent) {
      gapScale = 1;
      measuresXScale =
        totalNoteWidth > 0 ? (availableForContent - totalGapWidthDpr) / totalNoteWidth : 1;
    } else {
      const naturalContentWidth = totalNoteWidth + totalGapWidthDpr;
      measuresXScale = naturalContentWidth > 0 ? availableForContent / naturalContentWidth : 1;
      gapScale = measuresXScale;
    }
    this.measures.reduce(
      (prev, m, idx) =>
        prev +
        m.resize(
          measuresXScale,
          prev,
          pitchLineGap,
          m.measureData.firstNoteRelativeOffset * gapScale * dpr,
          idx === 0 ? extraWidth : 0,
        ),
      0,
    );
    if (this._data.clef === 'bass') this.setY(pitchLineGap * 4 + clefSpacing);
    this.positionExtraSymbols();
  }

  positionExtraSymbols() {
    if (this.clefSymbol) {
      this.clefSymbol.setOrigin(0, 0.5);
      const dpr = this.renderDpr;
      const symbolMargin = this.config.pitchLineGap * dpr * 0.5;
      const clefSymbolYPos =
        this._data.clef === 'bass' ? this.pitchLines[1].y : this.pitchLines[3].y + 3 * 1;
      this.clefSymbol.setPosition(symbolMargin, clefSymbolYPos);
      let nextSymbolX = this.clefSymbol.x + this.clefSymbol.displayWidth + symbolMargin;
      if (this.keySignatureGraphic) {
        this.keySignatureGraphic.resize(this.config.pitchLineGap, dpr);
        this.keySignatureGraphic.setPosition(nextSymbolX, 0);
        nextSymbolX += this.keySignatureGraphic.width + symbolMargin;
      }
      if (this.timeSignatureGraphics) {
        this.timeSignatureGraphics.forEach((item, idx) => {
          item.setOrigin(0.5, 0.5);
          item.setPosition(
            nextSymbolX + item.displayWidth / 2,
            this.pitchLines[3 - 2 * (1 - idx)].y,
          );
        });
      }
    }
  }

  getMeasures() {
    return this.measures;
  }

  highlightMeasure(measure: number) {
    this.measures[measure].highlight();
  }
}

export function getNoteNumber(note: BaseNote) {
  switch (note) {
    case 'C':
    case 'C#':
      return 0;
    case 'D':
    case 'D#':
      return 1;
    case 'E':
      return 2;
    case 'F':
    case 'F#':
      return 3;
    case 'G':
    case 'G#':
      return 4;
    case 'A':
    case 'A#':
      return 5;
    case 'B':
      return 6;
    default:
      console.error(`invalid base note: ${note}`);
      return null;
  }
}

export function getNoteLineNumber(
  octaveOffset: number,
  clef: ClefType,
  [step, octave]: [BaseNote, number],
) {
  const num = getNoteNumber(step);
  const oct = (octaveOffset - octave) * 7;
  const line = oct - num!;
  switch (clef) {
    case 'treble': {
      return line + 38;
    }
    case 'bass': {
      return line + 26;
    }
  }
}
